import { StackActions } from '@react-navigation/native';
import { IconSearch } from '@tabler/icons-react-native';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { searchObjectQuery, showSearchBar } from 'app/atoms';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { Box } from 'app/design/primitives/box';
import { SearchInput } from 'app/design/primitives/input/search';
import { useNavigation } from 'app/hooks/navigation';
import { useSmoothVisibility } from 'app/hooks/use-smooth-visibility';
import { SearchOverlay } from 'app/screens/billboard/landing-page/components/search-overlay';
import { navigateToAllListings } from 'app/screens/billboard/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import { TutorialRef } from 'app/types/tutorial';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useBreakpoint, useIsGuestWeb } from 'app/utils/web-helpers';

const theme = createDSTheme();

export function HeaderSearchBar() {
  const [searchQuery, setSearchQuery] = useAtom(searchObjectQuery);
  const navigation = useNavigation();
  const { viewRef: headerSearchBarRef } = useTutorialRef(TutorialRef.HeaderSearchBar);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [localQueryState, setLocalQueryState] = useState({ source: searchQuery, draft: searchQuery });
  const { t: tHeader } = useTranslation('common');

  let localQuery = localQueryState.draft;
  if (localQueryState.source !== searchQuery) {
    localQuery = searchQuery;
    setLocalQueryState({ source: searchQuery, draft: localQuery });
  }
  const setLocalQuery = (draft: string) => setLocalQueryState({ source: searchQuery, draft });

  const getCurrentListingsTabParams = () => {
    const state = navigation.getState();
    const currentRoute = state.routes[state.index];

    if (currentRoute?.name !== 'all-listings') {
      return { activeTab: ListingType.offer, type: ListingType.offer };
    }

    const params = currentRoute.params as { activeTab?: ListingType | 'people'; type?: ListingType } | undefined;
    const type = params?.type ?? ListingType.offer;

    return {
      activeTab: params?.activeTab ?? type,
      type,
    };
  };

  const onSubmit = (query: string) => {
    setIsOverlayVisible(false);
    setLocalQueryState({ source: query, draft: query });
    setSearchQuery(query);

    const state = navigation.getState();
    const { activeTab, type } = getCurrentListingsTabParams();

    if (state.index > 0) {
      navigation.dispatch(StackActions.popToTop());
    }

    navigateToAllListings(navigation, {
      activeTab,
      type,
      search: query || undefined,
    });
  };

  const isOverBreakpoint = useBreakpoint(1024);
  const isGuestWeb = useIsGuestWeb();
  const showSearchBarValue = useAtomValue(showSearchBar);
  const navigationState = navigation.getState();
  const currentRoute = navigationState.routes[navigationState.index];
  const isAllListingsRoute = currentRoute?.name === 'all-listings';

  const isVisible = isGuestWeb || (isOverBreakpoint && (!isAllListingsRoute || showSearchBarValue));

  const { animatedStyle } = useSmoothVisibility(isVisible);

  if (!isVisible) return null;

  const wrapperStyle = {
    flex: 1,
    marginLeft: isGuestWeb ? theme.spacing['spacing.general.x3'] : theme.spacing['spacing.general.x8'],
    marginRight: theme.spacing['spacing.general.x4'],
    maxWidth: 720,
  };

  return (
    <Animated.View style={[wrapperStyle, animatedStyle]}>
      <Box position="relative" width="100%" zIndex={20}>
        {isOverlayVisible ? (
          <Pressable
            onPress={() => setIsOverlayVisible(false)}
            style={{
              ...StyleSheet.absoluteFill,
              position: 'fixed' as 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}
          />
        ) : null}

        <Box ref={headerSearchBarRef} collapsable={false} position="relative" zIndex={2}>
          <SearchInput
            initialValue={searchQuery}
            onChangeText={setLocalQuery}
            submitIcon={
              <Pressable
                style={{
                  alignItems: 'center',
                  height: 28,
                  justifyContent: 'center',
                  width: 28,
                  backgroundColor: theme.colors['icon.primary'],
                  borderRadius: theme.borderRadii['radius.full'],
                }}
                onPress={() => onSubmit(localQuery.trim())}
              >
                <IconSearch
                  strokeWidth={ICON_STROKE_WIDTH * 2}
                  size={14}
                  color={theme.colors['icon.inverse.primary']}
                />
              </Pressable>
            }
            onFocus={() => setIsOverlayVisible(true)}
            onSubmit={onSubmit}
            backgroundColor={theme.colors['surface.default']}
            fieldBorderRadius="radius.xl"
            placeholder={tHeader('searchPlaceholder')}
            submitOnEmptyChange={false}
          />
        </Box>

        {isOverlayVisible ? (
          <Box position="absolute" top="100%" left={0} right={0} marginTop="spacing.general.x2" zIndex={3}>
            <SearchOverlay
              initialValue={searchQuery}
              onClose={() => setIsOverlayVisible(false)}
              onSubmit={onSubmit}
              onTagPress={(tagId) => {
                setIsOverlayVisible(false);
                navigateToAllListings(navigation, {
                  activeTab: ListingType.offer,
                  tagId,
                  type: ListingType.offer,
                });
              }}
              onViewAllListings={() => {
                setIsOverlayVisible(false);
                navigateToAllListings(navigation, {
                  activeTab: ListingType.offer,
                  type: ListingType.offer,
                });
              }}
              variant="dropdown"
              withSearchInput={false}
            />
          </Box>
        ) : null}
      </Box>
    </Animated.View>
  );
}
